/**
 * HubAiUseCaseWizard — a plain-language front door onto the AI Use Case register for people
 * who aren't privacy/compliance specialists. Standalone Hub page, same pattern as
 * HubSystemDescriptionPage/HubInitiativeTracker (its own top-level route, not nested under
 * HubLayout's category tree).
 *
 * Ported field-for-field from the reviewed HTML concept - the account owner iterated on this
 * exact structure, wording, and per-field flag placement across many rounds of feedback there,
 * and expects this real page to match it, not a plausible-looking approximation of it.
 *
 * Submitting creates a REAL record in the same register Privacy already uses, via the
 * existing aiUseCaseApi.create() — it lands in `draft` status (create_use_case has no
 * required-field validation; validate_for_submission only runs on the transition to
 * `submitted`, which this wizard never calls). The compliance-heavy fields this wizard
 * doesn't ask about (RACI, business case detail, AI Act classification, legal basis, ...)
 * stay blank for a reviewer to complete via the existing 8-step AiUseCaseWizard, at
 * /ai-use-cases/:id/intake/:step, before they submit it for real.
 *
 * Every "Continue"/"Submit for review" is unconditional — nothing here ever hard-blocks
 * progress on a flagged-but-incomplete field, by design. stepIsValid below only ever drives
 * the soft, non-blocking hint under the button, never whether it's clickable.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Select from 'react-select';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '../../components/PageHeader';
import { useWizardSteps } from '../../hooks/useWizardSteps';
import { useAuth } from '../../hooks/useAuth';
import { useToast } from '../../hooks/useToast';
import { reactSelectStyles } from '../../utils/reactSelectStyles';
import { aiUseCaseApi, aiUseCaseReviewApi, departmentsApi, reviewAssignmentsApi } from '../../services/api';
import type { AiUseCase, DataCategory, DataField, DataSensitivityAnswer, Department, ExpectedOutcome, VendorRecord } from '../../types/models';

// Eyebrow/title/help copy ported verbatim from the reviewed HTML concept. Icon is real-app
// addition (the concept had none) - matches the leading-icon convention every other
// .np-card-head in the app uses.
const STEP_META = [
  { eyebrow: 'Step 1 of 4', title: 'What are you trying to do?', icon: 'ri-lightbulb-line', help: "No jargon needed — just describe it like you'd explain it to a colleague. We'll help translate it into the right compliance language later." },
  { eyebrow: 'Step 2 of 4', title: 'What tool is this, and what data would it see?', icon: 'ri-database-2-line', help: "This is the bit that decides how closely Legal & Privacy need to look at it — so take your best guess, it doesn't need to be perfect." },
  { eyebrow: 'Step 3 of 4', title: 'Who else should know about this?', icon: 'ri-team-line', help: 'So the right people are looped in — this also helps whoever reviews it understand the blast radius if something needs to change.' },
  { eyebrow: 'Step 4 of 4', title: 'Quick check before this goes for review', icon: 'ri-checkbox-circle-line', help: "Here's everything as you've entered it. Fix anything, or just send it on." },
];

/** Horizontal step rail built entirely from real, already-established primitives -
 * .np-step-badge (the app's existing numbered/checkmark step indicator, e.g. a document's
 * per-section completion badge) connected by .np-progress/.np-fill segments (WizardShell's own
 * building blocks). A sidebar nav (WizardShell itself) isn't used here since this flow is short
 * and strictly linear, not a long form with sections worth jumping between - but the rail is
 * still built from the same real bricks as everywhere else, not invented from scratch. */
const WizardRail: React.FC<{ currentStepIndex: number }> = ({ currentStepIndex }) => (
  <div className="d-flex align-items-center mb-4">
    {STEP_META.map((step, i) => (
      <React.Fragment key={step.title}>
        <div className={`np-step-badge flex-shrink-0 ${i < currentStepIndex ? 'np-tone-success' : i === currentStepIndex ? '' : 'np-tone-neutral'}`}>
          <i className={i < currentStepIndex ? 'ri-check-line' : step.icon}></i>
        </div>
        {i < STEP_META.length - 1 && (
          <div className="np-progress flex-grow-1 mx-2">
            <div className={`np-fill ${i < currentStepIndex ? 'np-tone-success' : 'np-tone-neutral'}`} style={{ width: i < currentStepIndex ? '100%' : '0%' }} />
          </div>
        )}
      </React.Fragment>
    ))}
  </div>
);

// The backend stamps createdBy on every use case (create_use_case, lambda_function.py) but
// it predates the shared AiUseCase TS type needing to read it back - extended locally here
// rather than widening that type for one filter.
type AiUseCaseWithCreatedBy = AiUseCase & { createdBy?: string };

interface AiUseCaseReviewResult {
  status: 'generating' | 'complete' | 'failed';
  error?: string;
  precedentMatches?: { name: string; why: string }[];
  piiDisagreements?: { dataFieldName: string; why: string }[];
  inferredDataGaps?: { dataUsage: string; why: string }[];
  humanInvestigationChecklist?: string[];
  draftedFields?: Record<string, { value: string; rationale: string; confidence: string }>;
  // Advisory only, deliberately capped to genuinely critical items server-side - surfaced here
  // for a reviewer to act on via the real AI Risk Assessment flow, never auto-created as a
  // Risk-register row (that needs real likelihood/impact/mitigation judgment a plain
  // description can't supply).
  keyRisks?: { risk: string; why: string }[];
}

// The wizard's own third label for the tri-state answer - "not sure" reads better to an
// end-user than the register's own "Customer controlled", but stores as the same real
// DataSensitivityAnswer value underneath (never as a made-up fourth string - see
// to_sensitivity_answer() server-side, which silently downgrades anything unrecognised to
// 'no', the wrong direction for a genuinely uncertain answer).
const PII_LABELS: Record<DataSensitivityAnswer, string> = {
  yes: 'Identifies someone',
  no: "Doesn't identify anyone",
  customer_controlled: 'Not sure',
};
const PII_ANSWERS: DataSensitivityAnswer[] = ['yes', 'no', 'customer_controlled'];
const PII_BTN_TONE: Record<DataSensitivityAnswer, string> = { yes: 'np-btn-danger', no: 'np-btn-success', customer_controlled: 'np-btn-warning' };
const PII_CHIP_TONE: Record<DataSensitivityAnswer, string> = { yes: 'warning', no: 'neutral', customer_controlled: 'info' };

// Broad category question (Step 2) - matches the concept's DATA_CHOICES exactly, each mapped
// onto the real register's own DataCategory vocabulary for whatever gets itemized under it.
// "none"/"unsure" have no real-category mapping since nothing gets itemized under them.
const DATA_CHOICES: { key: string; title: string; desc: string; example?: string; realCategory?: DataCategory; icon: string; tone: string }[] = [
  { key: 'customer', title: 'Customer information', desc: 'Names, emails, purchase history, support tickets', example: 'customer email addresses, order history', realCategory: 'personal', icon: 'ri-user-3-line', tone: 'primary' },
  { key: 'employee', title: 'Colleague information', desc: 'HR records, performance, internal messages', example: 'staff names, HR records', realCategory: 'personal', icon: 'ri-contacts-line', tone: 'primary' },
  { key: 'financial', title: 'Financial data', desc: 'Invoices, payments, pricing, contracts', example: 'invoice numbers, contract values', realCategory: 'financial', icon: 'ri-money-dollar-circle-line', tone: 'primary' },
  { key: 'sensitive', title: 'Health or other sensitive details', desc: 'Anything that feels especially private', example: 'health details mentioned in a case note', realCategory: 'health', icon: 'ri-heart-pulse-line', tone: 'primary' },
  { key: 'none', title: 'None of these', desc: 'Just internal documents, code, or public information', icon: 'ri-forbid-2-line', tone: 'neutral' },
  { key: 'unsure', title: "I'm honestly not sure", desc: "That's OK — flag it below and someone will help", icon: 'ri-question-line', tone: 'warning' },
];
const NAMED_CATEGORY_KEYS = DATA_CHOICES.filter((c) => c.realCategory).map((c) => c.key);

interface DataPointDraft {
  key: string; // client-side only, for React list keys - never sent to the server
  text: string;
  isPersonalData: DataSensitivityAnswer;
}

// Every field the submitter can flag for help - one flag per field, not one per step, so
// confidence can vary question by question. submitterName/submitterEmail are never flaggable
// (there's nothing to be unsure about in your own name), so they're not in this list.
const FIELD_KEYS = [
  'title', 'description', 'businessValue', 'alternatives', 'tool', 'dataTypes', 'dataPoints',
  'automatedDecisions', 'department', 'owner', 'collaborators',
] as const;
type FieldKey = typeof FIELD_KEYS[number];

const FIELD_LABELS: Record<FieldKey, string> = {
  title: 'Give it a short name',
  description: 'What would it actually do, day to day?',
  businessValue: 'What would this actually save or improve?',
  alternatives: 'Did you consider doing this without AI?',
  tool: 'Which AI tool or product?',
  dataTypes: 'What kind of information would it be exposed to?',
  dataPoints: 'What specific data points would be involved?',
  automatedDecisions: 'Does it make decisions about people automatically?',
  department: 'Your team or department',
  owner: "Who's accountable for this if something goes wrong?",
  collaborators: "Anyone else involved, or who'd be affected?",
};

interface FlagState { flagged: boolean; note: string; }
const emptyFlags = (): Record<FieldKey, FlagState> =>
  FIELD_KEYS.reduce((acc, k) => ({ ...acc, [k]: { flagged: false, note: '' } }), {} as Record<FieldKey, FlagState>);

let idCounter = 0;
const nextId = () => `dp-${++idCounter}`;

/** The flag-for-help toggle sits next to every field's own label, not once per step - an
 * end-user unsure what a specific question is asking should be able to flag exactly that
 * question, not the whole page. Label matches the concept exactly: "Not sure?" / "🚩 Flagged". */
const FieldRow: React.FC<{
  fieldKey: FieldKey;
  number: number;
  label?: React.ReactNode;
  hint?: string;
  flags: Record<FieldKey, FlagState>;
  onFlagsChange: (next: Record<FieldKey, FlagState>) => void;
  children: React.ReactNode;
}> = ({ fieldKey, number, label, hint, flags, onFlagsChange, children }) => {
  const flag = flags[fieldKey];
  return (
    <div className="np-field mb-4">
      <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-2">
        <label className="mb-0"><span className="text-primary fw-bold me-1">{number}.</span>{label || FIELD_LABELS[fieldKey]}</label>
        <button
          type="button"
          className={`np-btn np-btn-sm ${flag.flagged ? 'np-btn-warning' : 'np-btn-ghost'}`}
          onClick={() => onFlagsChange({ ...flags, [fieldKey]: { ...flag, flagged: !flag.flagged } })}
        >
          <i className="ri-flag-line"></i> {flag.flagged ? 'Flagged' : 'Not sure?'}
        </button>
      </div>
      {hint && <p className="np-hint mt-1 mb-2">{hint}</p>}
      {children}
      {flag.flagged && (
        <textarea
          className="np-textarea mt-2"
          rows={2}
          placeholder="What are you unsure about here? Someone will follow up on this specifically."
          value={flag.note}
          onChange={(e) => onFlagsChange({ ...flags, [fieldKey]: { ...flag, note: e.target.value } })}
        />
      )}
    </div>
  );
};

/** Plain field wrapper with no flag toggle - for the submitter's own name/email, where "flag
 * this for help" doesn't mean anything. */
const PlainFieldRow: React.FC<{ label: string; hint?: string; children: React.ReactNode }> = ({ label, hint, children }) => (
  <div className="np-field mb-4">
    <label>{label}</label>
    {children}
    {hint && <p className="np-hint mt-1 mb-0">{hint}</p>}
  </div>
);

/** One category's itemized data-point list, once that category is checked in Step 2 - a real
 * table (point, the pill chosen, remove), same shape the reviewer's own summary uses later, so
 * it doesn't look like a different feature by the time it gets there. */
const DataPointCategoryGroup: React.FC<{
  categoryTitle: string;
  examplePlaceholder: string;
  points: DataPointDraft[];
  onAdd: (text: string, answer: DataSensitivityAnswer) => void;
  onRemove: (key: string) => void;
}> = ({ categoryTitle, examplePlaceholder, points, onAdd, onRemove }) => {
  const [text, setText] = useState('');
  const [answer, setAnswer] = useState<DataSensitivityAnswer | undefined>(undefined);

  const add = () => {
    const v = text.trim();
    if (!v) return;
    // Not answering isn't a hard stop, same as everywhere else in this wizard - it just lands
    // as "not sure", which is an honest answer, not a forced guess.
    onAdd(v, answer || 'customer_controlled');
    setText('');
    setAnswer(undefined);
  };

  return (
    <div className="np-card np-card-body mt-3" style={{ background: 'var(--np-surface-2)' }}>
      <div className="eyebrow-label">{categoryTitle}</div>
      {points.length > 0 && (
        <div className="np-table-wrap mb-2">
          <table className="np-table">
            <thead><tr><th>Data point</th><th>Identifies a person?</th><th></th></tr></thead>
            <tbody>
              {points.map((p) => (
                <tr key={p.key}>
                  <td>{p.text}</td>
                  <td><span className={`np-chip np-tone-${PII_CHIP_TONE[p.isPersonalData]}`}>{PII_LABELS[p.isPersonalData]}</span></td>
                  <td className="text-end">
                    <button type="button" className="np-icon-btn np-icon-btn-danger" onClick={() => onRemove(p.key)} title="Remove">
                      <i className="ri-close-line"></i>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div className="d-flex gap-2 mb-2">
        <input
          className="np-input"
          placeholder={examplePlaceholder}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
        />
        <button type="button" className="np-btn np-btn-ghost" style={{ flex: 'none' }} onClick={add}>Add</button>
      </div>
      <div className="d-flex align-items-center gap-2 flex-wrap">
        <span className="np-hint mb-0">Does this identify a specific person?</span>
        {PII_ANSWERS.map((a) => (
          <button
            key={a}
            type="button"
            className={`np-btn np-btn-sm ${answer === a ? PII_BTN_TONE[a] : 'np-btn-ghost'}`}
            onClick={() => setAnswer((prev) => (prev === a ? undefined : a))}
          >
            {PII_LABELS[a]}
          </button>
        ))}
      </div>
    </div>
  );
};

const THINKING_LOG = [
  'Reading your submission…',
  'Comparing against existing use cases on file…',
  'Checking against GDPR requirements…',
  'Checking against EU AI Act risk categories…',
  'Preparing a summary for review…',
];

export const HubAiUseCaseWizard: React.FC = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const { user } = useAuth();

  const [submittedUseCaseId, setSubmittedUseCaseId] = useState<string | null>(null);

  const [submitterName, setSubmitterName] = useState(user?.name || '');
  const [submitterEmail, setSubmitterEmail] = useState(user?.email || '');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [businessValueAnswer, setBusinessValueAnswer] = useState('');
  const [alternativesAnswer, setAlternativesAnswer] = useState('');
  const [tool, setTool] = useState('');
  const [dataTypes, setDataTypes] = useState<string[]>([]);
  const [dataPoints, setDataPoints] = useState<Record<string, DataPointDraft[]>>({});
  const [departmentId, setDepartmentId] = useState('');
  // Defaults to the submitter's own name - for a self-service submission like this, the
  // submitter usually is the de facto accountable person, but it's a real name field, not a
  // fixed identity, so it stays independently editable rather than locked to submitterName.
  const [owner, setOwner] = useState(user?.name || '');
  const [collaborators, setCollaborators] = useState('');
  const [makesAutomatedDecisions, setMakesAutomatedDecisions] = useState(false);
  const [involvesProfiling, setInvolvesProfiling] = useState(false);
  const [automatedDecisionDetailsAnswer, setAutomatedDecisionDetailsAnswer] = useState('');
  const [flags, setFlags] = useState<Record<FieldKey, FlagState>>(emptyFlags());

  const { currentStepIndex, goTo, goNext, goBack, isFirst, isLast } = useWizardSteps(STEP_META.length);

  const { data: departments = [] } = useQuery<Department[]>({
    queryKey: ['departments'],
    queryFn: departmentsApi.getAll,
  });
  const departmentOptions = useMemo(
    () => departments.map((d) => ({ value: d.departmentId, label: d.name })),
    [departments],
  );

  const { data: myUseCases = [] } = useQuery<AiUseCaseWithCreatedBy[]>({
    queryKey: ['aiUseCases'],
    queryFn: aiUseCaseApi.list,
  });
  // No in-app notification system exists yet - this is how a submitter finds their own
  // request again.
  const mySubmissions = myUseCases.filter((uc) => uc.createdBy === user?.userId);

  const toggleDataType = (key: string) => {
    setDataTypes((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));
  };
  const addDataPoint = (categoryKey: string, text: string, answer: DataSensitivityAnswer) => {
    setDataPoints((prev) => ({ ...prev, [categoryKey]: [...(prev[categoryKey] || []), { key: nextId(), text, isPersonalData: answer }] }));
  };
  const removeDataPoint = (categoryKey: string, pointKey: string) => {
    setDataPoints((prev) => ({ ...prev, [categoryKey]: (prev[categoryKey] || []).filter((p) => p.key !== pointKey) }));
  };

  const namedCategoriesChecked = dataTypes.filter((k) => NAMED_CATEGORY_KEYS.includes(k));

  // Nothing here is ever a hard gate - leaving a field blank (flagged or not) is always a
  // valid way to move on. This only drives the soft, non-blocking hint below the button.
  const stepIsValid = (stepIndex: number): boolean => {
    if (stepIndex === 0) return title.trim().length > 0 && description.trim().length > 0;
    if (stepIndex === 1) return tool.trim().length > 0 && dataTypes.length > 0;
    if (stepIndex === 2) return departmentId.trim().length > 0;
    return true;
  };

  const submitMutation = useMutation({
    mutationFn: async () => {
      const dataFields: DataField[] = [];
      namedCategoriesChecked.forEach((catKey) => {
        const choice = DATA_CHOICES.find((c) => c.key === catKey);
        (dataPoints[catKey] || []).forEach((p) => {
          dataFields.push({
            id: '', // server mints a real id when this is empty
            name: p.text,
            category: choice?.realCategory || 'other',
            isPersonalData: p.isPersonalData,
            isSpecialCategory: false,
            sensitivity: 'internal',
            processingPurpose: '',
            dataSource: '',
          });
        });
      });

      // The submitter is always someone who "uses the AI output and needs to know it exists"
      // (RACI-Informed's own definition) - stated directly here rather than left for Bedrock
      // to infer, since the wizard already has this identity from Step 1 with certainty an
      // inference never would.
      const raciInformed = [
        submitterName.trim() ? `${submitterName.trim()} (submitter)` : '',
        collaborators.trim(),
      ].filter(Boolean).join('; ');

      const payload: Partial<AiUseCase> = {
        name: title.trim() || 'Untitled',
        businessProblem: description,
        // Sent as the submitter's own words, not required to be thorough - the Bedrock review
        // expands both into the fuller paragraph the register field actually wants, using this
        // plus the rest of the submission as context. Left blank rather than invented if the
        // submitter left it blank too.
        businessValue: businessValueAnswer,
        alternatives: alternativesAnswer,
        departmentId,
        owner,
        raciInformed,
        dataFields,
        makesAutomatedDecisions,
        involvesProfiling,
        automatedDecisionDetails: makesAutomatedDecisions ? automatedDecisionDetailsAnswer : '',
        // A tool/vendor being named at all means data is, by definition, going to an external
        // AI provider - deterministic from data already collected, not worth an AI guess.
        thirdPartySharing: Boolean(tool.trim()),
        vendors: tool.trim()
          ? ([{
              id: '', name: tool.trim(), modelName: '', hostingRegion: '', dpaReference: '',
              contractExpiry: '', trainsOnOurData: false, subProcessors: '',
            }] satisfies VendorRecord[])
          : [],
      };
      const created = await aiUseCaseApi.create(payload);

      // A primary review assignment is always created, unassigned - an admin triages from
      // the Review tab even when nothing was flagged. Best-effort: a failure here shouldn't
      // block the submission itself, which already exists as a real record at this point.
      try {
        await reviewAssignmentsApi.create({ entityType: 'ai_use_case', entityId: created.useCaseId, scope: 'primary' });
        await Promise.all(
          FIELD_KEYS.filter((k) => flags[k].flagged).map((k) =>
            reviewAssignmentsApi.create({
              entityType: 'ai_use_case',
              entityId: created.useCaseId,
              scope: 'field',
              fieldKey: k,
              note: flags[k].note.trim() || `Flagged: ${FIELD_LABELS[k]}`,
            }),
          ),
        );
      } catch (e) {
        console.error('Review assignment creation failed (non-fatal):', e);
      }

      try {
        await aiUseCaseReviewApi.start(created.useCaseId);
      } catch (e) {
        console.error('Starting the AI review failed (non-fatal):', e);
      }

      return created;
    },
    onSuccess: (created) => {
      setSubmittedUseCaseId(created.useCaseId);
      setThinkingLineCount(0);
      queryClient.invalidateQueries({ queryKey: ['aiUseCases'] });
    },
    onError: (e: Error) => toast.error(`Couldn't submit this: ${e.message}`),
  });

  const { data: reviewResp } = useQuery<{ review: AiUseCaseReviewResult }>({
    queryKey: ['aiUseCaseReview', submittedUseCaseId],
    queryFn: () => aiUseCaseReviewApi.get(submittedUseCaseId!),
    enabled: !!submittedUseCaseId,
    refetchInterval: (query) => (query.state.data?.review?.status === 'generating' ? 2500 : false),
    retry: false,
  });
  const review = reviewResp?.review;

  // Derived directly from data, not tracked as its own state: 'wizard' until something's been
  // submitted, then 'thinking' while the review is still generating (or hasn't loaded yet)
  // and 'results' once it settles either way.
  const screen: 'wizard' | 'thinking' | 'results' = !submittedUseCaseId
    ? 'wizard'
    : (!review || review.status === 'generating')
      ? 'thinking'
      : 'results';

  // Once the review lands, Bedrock's drafted outcomes have already been auto-applied straight
  // onto the real record (same mechanism as every other drafted field) - re-fetching it here is
  // what surfaces them for editing, rather than trusting a copy off the review response, which
  // exists for display of the advisory findings, not as the source of truth for the record.
  const { data: submittedUseCase } = useQuery<AiUseCase>({
    queryKey: ['aiUseCase', submittedUseCaseId],
    queryFn: () => aiUseCaseApi.get(submittedUseCaseId!),
    enabled: !!submittedUseCaseId && screen === 'results',
  });

  const [draftOutcomes, setDraftOutcomes] = useState<ExpectedOutcome[]>([]);
  // Seed the editable draft from the fetched record exactly once per submission - a render-time
  // adjustment (React's documented pattern for "reset state when an id changes"), not an effect,
  // since setting state unconditionally in an effect body here would cascade on every refetch.
  const [outcomesSeededFor, setOutcomesSeededFor] = useState<string | null>(null);
  if (submittedUseCase && outcomesSeededFor !== submittedUseCaseId) {
    setOutcomesSeededFor(submittedUseCaseId);
    setDraftOutcomes(submittedUseCase.outcomes || []);
  }
  const outcomesSeeded = outcomesSeededFor === submittedUseCaseId && !!submittedUseCase;

  const saveOutcomesMutation = useMutation({
    mutationFn: () => aiUseCaseApi.update(submittedUseCaseId!, { outcomes: draftOutcomes }),
    onSuccess: () => {
      toast.success('Outcomes saved');
      queryClient.invalidateQueries({ queryKey: ['aiUseCase', submittedUseCaseId] });
    },
    onError: (e: Error) => toast.error(`Couldn't save outcomes: ${e.message}`),
  });

  // Purely a reassuring "here's roughly what's happening" indicator while the real poll above
  // runs - reveals one line at a time, independent of when the real result actually lands (the
  // screen above already switches to 'results' based on real state, not this).
  const [thinkingLineCount, setThinkingLineCount] = useState(0);
  useEffect(() => {
    if (screen !== 'thinking' || thinkingLineCount >= THINKING_LOG.length) return;
    const t = setTimeout(() => setThinkingLineCount((n) => n + 1), 900);
    return () => clearTimeout(t);
  }, [screen, thinkingLineCount]);

  const startNewSubmission = () => {
    setSubmitterName(user?.name || ''); setSubmitterEmail(user?.email || '');
    setTitle(''); setDescription(''); setBusinessValueAnswer(''); setAlternativesAnswer('');
    setTool(''); setDataTypes([]); setDataPoints({});
    setMakesAutomatedDecisions(false); setInvolvesProfiling(false); setAutomatedDecisionDetailsAnswer('');
    setDepartmentId(''); setOwner(user?.name || ''); setCollaborators(''); setFlags(emptyFlags());
    setSubmittedUseCaseId(null);
    setThinkingLineCount(0);
    setDraftOutcomes([]); setOutcomesSeededFor(null);
    goTo(0);
  };

  const flaggedCount = FIELD_KEYS.filter((k) => flags[k].flagged).length;

  /** One review-step block: a title, its rows (each optionally tied to a flaggable field), and
   * any flagged-note callouts for rows in this block that were flagged. */
  const reviewBlock = (blockTitle: string, rows: { label: string; value: string; fieldKey?: FieldKey; long?: boolean; icon?: string }[]) => {
    const flaggedRows = rows.filter((r) => r.fieldKey && flags[r.fieldKey].flagged);
    return (
      <div className="np-card mb-3" key={blockTitle}>
        <div className="np-card-head">
          <h2>{blockTitle}</h2>
          {flaggedRows.length > 0 && <span className="np-chip np-tone-warning">🚩 {flaggedRows.length} flagged</span>}
        </div>
        <div className="np-card-body">
          <div>
            {rows.map((r) =>
              r.long ? (
                <div className="mb-3" key={r.label}>
                  <div className="eyebrow-label mb-1">{r.label}{r.fieldKey && flags[r.fieldKey].flagged ? ' 🚩' : ''}</div>
                  <p className="mb-0">{r.value || '—'}</p>
                </div>
              ) : (
                <div className="np-meta-row" key={r.label}>
                  <span className="np-meta-label">{r.icon && <i className={r.icon}></i>}{r.label}{r.fieldKey && flags[r.fieldKey].flagged ? ' 🚩' : ''}</span>
                  <span className="np-meta-value">{r.value || '—'}</span>
                </div>
              ),
            )}
          </div>
          {flaggedRows.map((r) => {
            const note = flags[r.fieldKey!].note;
            return note ? (
              <div key={r.label} className="np-banner np-tone-warning mt-2" style={{ fontSize: '0.78rem' }}>
                <i className="ri-flag-line"></i>
                <span>{r.label} — note for the reviewer: {note}</span>
              </div>
            ) : null;
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="container-xxl flex-grow-1 container-p-y np-page">
      <nav className="np-crumb mb-2">
        <Link to="/hub">Hub</Link>
        <span className="np-crumb-sep">›</span>
        <span className="np-crumb-current">Propose an AI Use Case</span>
      </nav>

      <PageHeader
        icon="ri-lightbulb-line"
        title="Propose an AI Use Case"
        subtitle="Thinking of using an AI tool for something at work? Log it here — someone from Privacy & Compliance will review it and let you know what's needed to move forward."
      />

      {screen === 'wizard' && (
        <>
          <div className="np-card">
            <div className="np-card-body">
              <WizardRail currentStepIndex={currentStepIndex} />

              <div className="np-card-head px-0 pt-0 mb-3">
                <span className="np-icon-badge np-tone-primary"><i className={STEP_META[currentStepIndex].icon}></i></span>
                <div>
                  <div className="eyebrow-label mb-1">{STEP_META[currentStepIndex].eyebrow}</div>
                  <h2>{STEP_META[currentStepIndex].title}</h2>
                </div>
              </div>
              <p className="np-sub mb-4">{STEP_META[currentStepIndex].help}</p>

              {currentStepIndex === 0 && (
                <>
                  <div style={{ fontSize: '0.72rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.03em', color: 'var(--bs-secondary-color, var(--bs-body-color))', marginBottom: '0.6rem' }}>
                    About you
                  </div>
                  <PlainFieldRow label="Your name">
                    <input className="np-input" placeholder="e.g. “Priya Shah”" value={submitterName} onChange={(e) => setSubmitterName(e.target.value)} />
                  </PlainFieldRow>
                  <PlainFieldRow label="Your email" hint="So whoever reviews this — or picks up a flagged question — knows how to reach you.">
                    <input className="np-input" type="email" placeholder="e.g. “priya.shah@mercell.com”" value={submitterEmail} onChange={(e) => setSubmitterEmail(e.target.value)} />
                  </PlainFieldRow>

                  <FieldRow fieldKey="title" number={1} flags={flags} onFlagsChange={setFlags} hint="A few words is fine — you'll explain more below.">
                    <input className="np-input" placeholder="e.g. “AI note-taker for client calls”" value={title} onChange={(e) => setTitle(e.target.value)} />
                  </FieldRow>
                  <FieldRow fieldKey="description" number={2} flags={flags} onFlagsChange={setFlags}>
                    <textarea
                      className="np-textarea"
                      placeholder="e.g. “It would listen to my sales calls and write a summary afterwards so I don't have to take notes.”"
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                    />
                  </FieldRow>

                  <FieldRow
                    fieldKey="businessValue"
                    number={3}
                    flags={flags}
                    onFlagsChange={setFlags}
                    hint="A rough guess is fine — time saved, fewer mistakes, faster turnaround, whatever comes to mind."
                  >
                    <textarea
                      className="np-textarea"
                      placeholder="e.g. “Probably saves me a couple of hours a week, and I'd stop missing details from calls.”"
                      value={businessValueAnswer}
                      onChange={(e) => setBusinessValueAnswer(e.target.value)}
                    />
                  </FieldRow>

                  <FieldRow
                    fieldKey="alternatives"
                    number={4}
                    flags={flags}
                    onFlagsChange={setFlags}
                    hint="Doesn't need to be thorough — just what crossed your mind, if anything."
                  >
                    <textarea
                      className="np-textarea"
                      placeholder="e.g. “I could keep taking notes by hand, but I looked into it and this seemed a lot faster.”"
                      value={alternativesAnswer}
                      onChange={(e) => setAlternativesAnswer(e.target.value)}
                    />
                  </FieldRow>
                </>
              )}

              {currentStepIndex === 1 && (
                <>
                  <FieldRow fieldKey="tool" number={1} flags={flags} onFlagsChange={setFlags}>
                    <input className="np-input" placeholder="e.g. “Otter.ai”, “ChatGPT”, “a feature inside our CRM”" value={tool} onChange={(e) => setTool(e.target.value)} />
                  </FieldRow>

                  <FieldRow
                    fieldKey="dataTypes"
                    number={2}
                    label={<>What kind of information would it be exposed to? <span style={{ fontWeight: 400, color: 'var(--bs-secondary-color, var(--bs-body-color))' }}>(pick all that apply)</span></>}
                    flags={flags}
                    onFlagsChange={setFlags}
                  >
                    <div className="np-grid np-grid-2">
                      {DATA_CHOICES.map((c) => {
                        const checked = dataTypes.includes(c.key);
                        return (
                          <label key={c.key} className={`np-choice${checked ? ' selected np-tone-primary' : ''}`}>
                            <span className={`np-icon-badge np-tone-${c.tone}`}><i className={c.icon}></i></span>
                            <div className="np-choice-body flex-grow-1">
                              <div className="d-flex align-items-center justify-content-between gap-2">
                                <div className="np-choice-label">{c.title}</div>
                                <input type="checkbox" checked={checked} onChange={() => toggleDataType(c.key)} />
                              </div>
                              <p className="np-choice-desc">{c.desc}</p>
                            </div>
                          </label>
                        );
                      })}
                    </div>
                  </FieldRow>

                  {namedCategoriesChecked.length > 0 && (
                    <FieldRow
                      fieldKey="dataPoints"
                      number={3}
                      flags={flags}
                      onFlagsChange={setFlags}
                      hint="Add as many as come to mind — press Enter or Add after each one. A rough list is fine."
                    >
                      {namedCategoriesChecked.map((catKey) => {
                        const choice = DATA_CHOICES.find((c) => c.key === catKey)!;
                        return (
                          <DataPointCategoryGroup
                            key={catKey}
                            categoryTitle={choice.title}
                            examplePlaceholder={`e.g. ${choice.example || 'a specific field'}`}
                            points={dataPoints[catKey] || []}
                            onAdd={(text, answer) => addDataPoint(catKey, text, answer)}
                            onRemove={(pointKey) => removeDataPoint(catKey, pointKey)}
                          />
                        );
                      })}
                    </FieldRow>
                  )}

                  <FieldRow
                    fieldKey="automatedDecisions"
                    number={4}
                    flags={flags}
                    onFlagsChange={setFlags}
                    hint="For example, automatically approving, rejecting, prioritizing or flagging something about a specific person."
                  >
                    <div className="d-flex gap-2">
                      <button
                        type="button"
                        className={`np-btn np-btn-sm ${makesAutomatedDecisions ? 'np-btn-primary' : 'np-btn-ghost'}`}
                        onClick={() => setMakesAutomatedDecisions(true)}
                      >
                        Yes
                      </button>
                      <button
                        type="button"
                        className={`np-btn np-btn-sm ${!makesAutomatedDecisions ? 'np-btn-primary' : 'np-btn-ghost'}`}
                        onClick={() => { setMakesAutomatedDecisions(false); setInvolvesProfiling(false); setAutomatedDecisionDetailsAnswer(''); }}
                      >
                        No
                      </button>
                    </div>
                    {makesAutomatedDecisions && (
                      <div className="np-card np-card-body mt-3" style={{ background: 'var(--np-surface-2)' }}>
                        <PlainFieldRow label="What does it decide, and what could someone do about it?">
                          <textarea
                            className="np-textarea"
                            placeholder="e.g. “It would flag support tickets as high-priority automatically — someone could still manually re-prioritize any ticket.”"
                            value={automatedDecisionDetailsAnswer}
                            onChange={(e) => setAutomatedDecisionDetailsAnswer(e.target.value)}
                          />
                        </PlainFieldRow>
                        <div className="d-flex align-items-center gap-2 flex-wrap">
                          <span className="np-hint mb-0">Does it also build or use a profile of people to do this?</span>
                          <button
                            type="button"
                            className={`np-btn np-btn-sm ${involvesProfiling ? 'np-btn-primary' : 'np-btn-ghost'}`}
                            onClick={() => setInvolvesProfiling(true)}
                          >
                            Yes
                          </button>
                          <button
                            type="button"
                            className={`np-btn np-btn-sm ${!involvesProfiling ? 'np-btn-primary' : 'np-btn-ghost'}`}
                            onClick={() => setInvolvesProfiling(false)}
                          >
                            No
                          </button>
                        </div>
                      </div>
                    )}
                  </FieldRow>
                </>
              )}

              {currentStepIndex === 2 && (
                <>
                  <FieldRow fieldKey="department" number={1} flags={flags} onFlagsChange={setFlags}>
                    <Select
                      inputId="hub-aiuc-department"
                      options={departmentOptions}
                      value={departmentOptions.find((o) => o.value === departmentId) || null}
                      onChange={(opt) => setDepartmentId(opt?.value || '')}
                      placeholder="Select a department…"
                      styles={reactSelectStyles()}
                      isClearable
                    />
                  </FieldRow>
                  <FieldRow fieldKey="owner" number={2} flags={flags} onFlagsChange={setFlags} hint="Defaults to you — change it if someone else should own this.">
                    <input className="np-input" placeholder="e.g. “Priya Shah”" value={owner} onChange={(e) => setOwner(e.target.value)} />
                  </FieldRow>
                  <FieldRow fieldKey="collaborators" number={3} flags={flags} onFlagsChange={setFlags}>
                    <textarea
                      className="np-textarea"
                      placeholder="e.g. “My manager knows I'm looking into this. Our support team would be the ones actually using it.”"
                      value={collaborators}
                      onChange={(e) => setCollaborators(e.target.value)}
                    />
                  </FieldRow>
                </>
              )}

              {currentStepIndex === 3 && (
                <>
                  {reviewBlock('About you', [
                    { label: 'Your name', value: submitterName, icon: 'ri-user-line' },
                    { label: 'Your email', value: submitterEmail, icon: 'ri-mail-line' },
                  ])}
                  {reviewBlock('What & why', [
                    { label: 'Use case name', value: title, fieldKey: 'title', icon: 'ri-price-tag-3-line' },
                    { label: 'Description', value: description, fieldKey: 'description', long: true },
                    { label: 'Expected value', value: businessValueAnswer, fieldKey: 'businessValue', long: true },
                    { label: 'Alternatives considered', value: alternativesAnswer, fieldKey: 'alternatives', long: true },
                  ])}
                  {reviewBlock('Tool & data', [
                    { label: 'Tool', value: tool, fieldKey: 'tool', icon: 'ri-tools-line' },
                    { label: 'Data involved', value: dataTypes.map((k) => DATA_CHOICES.find((c) => c.key === k)?.title || k).join(', '), fieldKey: 'dataTypes', long: true },
                    { label: 'Specific data points', value: Object.values(dataPoints).flat().map((p) => p.text).join(', '), fieldKey: 'dataPoints', long: true },
                    {
                      label: 'Automated decisions about people',
                      value: makesAutomatedDecisions
                        ? `Yes${involvesProfiling ? ' (includes profiling)' : ''}${automatedDecisionDetailsAnswer ? ' — ' + automatedDecisionDetailsAnswer : ''}`
                        : 'No',
                      fieldKey: 'automatedDecisions',
                      long: true,
                    },
                  ])}
                  {reviewBlock("Who's involved", [
                    { label: 'Department', value: departmentOptions.find((o) => o.value === departmentId)?.label || '', fieldKey: 'department', icon: 'ri-building-line' },
                    { label: 'Accountable if something goes wrong', value: owner, fieldKey: 'owner', icon: 'ri-user-star-line' },
                    { label: 'Others involved', value: collaborators, fieldKey: 'collaborators', long: true },
                  ])}
                  {flaggedCount > 0 && (
                    <div className="np-banner np-tone-warning">
                      <i className="ri-flag-line"></i>
                      <span>Whoever reviews this will see exactly which sections you flagged, with your notes — you don't need to explain everything perfectly.</span>
                    </div>
                  )}
                </>
              )}

              {!stepIsValid(currentStepIndex) && (
                <p className="np-hint" style={{ margin: '-0.4rem 0 0.9rem' }}>
                  Nothing here is required to continue — leave anything blank if you're not sure, and flag it above if you'd like input.
                </p>
              )}

              <div className="d-flex align-items-center justify-content-between mt-2 pt-3 border-top">
                <button type="button" className="np-btn np-btn-ghost" onClick={goBack} style={{ visibility: isFirst ? 'hidden' : 'visible' }}>
                  <i className="ri-arrow-left-line"></i> Back
                </button>
                {!isLast && (
                  <button type="button" className="np-btn np-btn-primary" onClick={goNext}>
                    Continue <i className="ri-arrow-right-line"></i>
                  </button>
                )}
                {isLast && (
                  <button type="button" className="np-btn np-btn-primary" disabled={submitMutation.isPending} onClick={() => submitMutation.mutate()}>
                    <i className="ri-send-plane-line"></i> {submitMutation.isPending ? 'Submitting…' : 'Submit for review'}
                  </button>
                )}
              </div>
            </div>
          </div>

          <p className="np-hint d-flex align-items-start gap-2 mt-3" style={{ fontSize: '0.72rem' }}>
            <i className="ri-information-line"></i>
            <span>Nothing on this form ever blocks you from moving forward or submitting — if you're unsure about anything, flag it and someone will follow up. There's no wrong answer here.</span>
          </p>
        </>
      )}

      {screen === 'thinking' && (
        <div className="np-card">
          <div className="np-card-body text-center py-5">
            <div className="spinner-border text-primary mb-3" role="status" style={{ width: '3rem', height: '3rem' }}>
              <span className="visually-hidden">Loading…</span>
            </div>
            <h2 className="mb-1">Bedrock is reviewing your submission</h2>
            <p className="np-sub">This usually takes under a minute.</p>
            <ul className="list-unstyled text-start mt-3 mb-0 mx-auto" style={{ maxWidth: '22rem', fontSize: '0.8rem' }}>
              {THINKING_LOG.slice(0, thinkingLineCount).map((line, i) => (
                <li key={i} className="d-flex align-items-center gap-2 mb-2">
                  <i className="ri-check-line text-success"></i>{line}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {screen === 'results' && (
        <>
          <div className="np-banner np-tone-success mb-3">
            <i className="ri-checkbox-circle-line"></i>
            <div>
              <strong style={{ display: 'block', fontSize: '0.92rem', marginBottom: '0.2rem' }}>Submitted for review</strong>
              <span>
                An admin will assign <strong>{title || 'this'}</strong> to the right reviewer
                {flaggedCount > 0 ? `, and route the ${flaggedCount} question${flaggedCount === 1 ? '' : 's'} you flagged to whoever can answer them` : ''}.
                You'll see how it's progressing right here in the Hub — usually within a few
                working days. No action needed from you right now.
              </span>
            </div>
          </div>

          <div className="np-card mb-3">
            <div className="np-card-body">
              <h4 style={{ fontSize: '0.85rem', marginBottom: '0.9rem' }}>What happens next</h4>
              <div className="np-timeline-item">
                <span className="np-timeline-dot" style={{ background: 'var(--bs-success)' }}></span>
                <div className="np-timeline-title">You submitted this use case</div>
                <div className="np-timeline-meta">Just now</div>
              </div>
              <div className="np-timeline-item">
                <span className="np-timeline-dot" style={{ background: review?.status === 'failed' ? 'var(--bs-warning)' : 'var(--bs-success)' }}></span>
                <div className="np-timeline-title">AI reviewed it for GDPR &amp; AI Act alignment</div>
                <div className="np-timeline-meta">
                  {review?.status === 'failed'
                    ? "Couldn't finish automatically — a human reviewer will look at this directly instead"
                    : 'Just now — attached for whoever reviews this'}
                </div>
              </div>
              <div className="np-timeline-item">
                <span className="np-timeline-dot" style={{ background: 'var(--np-border-strong)' }}></span>
                <div className="np-timeline-title">An admin assigns it to a reviewer</div>
                <div className="np-timeline-meta">
                  {flaggedCount > 0 ? 'You flagged something — that question may go to a specific person to answer' : 'Usually within a day or two'}
                </div>
              </div>
              <div className="np-timeline-item">
                <span className="np-timeline-dot" style={{ background: 'var(--np-border-strong)' }}></span>
                <div className="np-timeline-title">The reviewer decides</div>
                <div className="np-timeline-meta">Usually a few working days</div>
              </div>
              <div className="np-timeline-item" style={{ borderLeftColor: 'transparent' }}>
                <span className="np-timeline-dot" style={{ background: 'var(--np-border-strong)' }}></span>
                <div className="np-timeline-title">You'll see the outcome here</div>
                <div className="np-timeline-meta">Approved, approved with conditions, or they'll ask you to adjust something</div>
              </div>
            </div>
          </div>

          {review?.status === 'complete' && (
            <div className="np-card mb-3">
              <div className="np-card-head">
                <span className="np-icon-badge np-tone-primary"><i className="ri-sparkling-2-line"></i></span>
                <div>
                  <h2>AI-assisted review</h2>
                  <p className="np-sub mb-0">Generated by Bedrock — one pass over your request, advisory only</p>
                </div>
              </div>
              <div className="np-card-body">
                {(review.piiDisagreements?.length ?? 0) > 0 && (
                  <div className="np-banner np-tone-warning mb-3">
                    <i className="ri-eye-line"></i>
                    <span>
                      {review.piiDisagreements?.length} of the data points you marked as not
                      identifying anyone might actually be personal data - your reviewer will
                      confirm this.
                    </span>
                  </div>
                )}
                {(review.keyRisks?.length ?? 0) > 0 && (
                  <div className="mb-3">
                    <div className="eyebrow-label">Worth flagging as a real risk</div>
                    <div className="np-list">
                      {review.keyRisks?.map((r, i) => (
                        <div className="np-list-item" key={i}>
                          <span>
                            <i className="ri-alarm-warning-line me-2 text-danger"></i>
                            {r.risk}
                            {r.why && <span className="np-sub d-block mt-1">{r.why}</span>}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {(review.inferredDataGaps?.length ?? 0) > 0 && (
                  <div className="mb-3">
                    <div className="eyebrow-label">Data usage worth double-checking</div>
                    <div className="np-list">
                      {review.inferredDataGaps?.map((g, i) => (
                        <div className="np-list-item" key={i}>
                          <span><i className="ri-question-line me-2 text-warning"></i>{g.dataUsage}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {(review.humanInvestigationChecklist?.length ?? 0) > 0 && (
                  <div>
                    <div className="eyebrow-label">Still needs a human answer</div>
                    <div className="np-list">
                      {review.humanInvestigationChecklist?.map((c, i) => (
                        <div className="np-list-item" key={i}>
                          <span><i className="ri-user-search-line me-2 text-secondary"></i>{c}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {!review.piiDisagreements?.length && !review.keyRisks?.length && !review.inferredDataGaps?.length && !review.humanInvestigationChecklist?.length && (
                  <p className="np-sub mb-0">Nothing stood out - this looks straightforward.</p>
                )}
              </div>
            </div>
          )}

          {review?.status === 'complete' && outcomesSeeded && (
            <div className="np-card mb-3">
              <div className="np-card-head">
                <span className="np-icon-badge np-tone-primary"><i className="ri-flag-2-line"></i></span>
                <div>
                  <h2>Expected outcomes</h2>
                  <p className="np-sub mb-0">
                    {draftOutcomes.length > 0
                      ? "How you'll know this is working — drafted from what you told us, edit anything that's off"
                      : "How will you know this is working? Add one or two if you can — it helps whoever reviews this."}
                  </p>
                </div>
              </div>
              <div className="np-card-body">
                {draftOutcomes.map((o, i) => (
                  <div className="row g-2 mb-2" key={i}>
                    <div className="col-md-6">
                      <input
                        className="np-input"
                        placeholder="Outcome, e.g. “Faster response to high-priority tickets”"
                        value={o.outcome}
                        onChange={(e) => setDraftOutcomes((prev) => prev.map((p, pi) => (pi === i ? { ...p, outcome: e.target.value } : p)))}
                      />
                    </div>
                    <div className="col-md-5">
                      <input
                        className="np-input"
                        placeholder="Metric, e.g. “Time from ticket creation to first response”"
                        value={o.metric}
                        onChange={(e) => setDraftOutcomes((prev) => prev.map((p, pi) => (pi === i ? { ...p, metric: e.target.value } : p)))}
                      />
                    </div>
                    <div className="col-md-1 d-flex align-items-center">
                      <button
                        type="button"
                        className="np-btn np-btn-ghost np-btn-sm"
                        onClick={() => setDraftOutcomes((prev) => prev.filter((_, pi) => pi !== i))}
                        aria-label="Remove outcome"
                      >
                        <i className="ri-close-line"></i>
                      </button>
                    </div>
                  </div>
                ))}
                <div className="d-flex gap-2 mt-2">
                  <button
                    type="button"
                    className="np-btn np-btn-ghost np-btn-sm"
                    onClick={() => setDraftOutcomes((prev) => [...prev, { id: '', outcome: '', metric: '', targetDate: '' }])}
                  >
                    <i className="ri-add-line"></i> Add outcome
                  </button>
                  <button
                    type="button"
                    className="np-btn np-btn-primary np-btn-sm"
                    disabled={saveOutcomesMutation.isPending}
                    onClick={() => saveOutcomesMutation.mutate()}
                  >
                    {saveOutcomesMutation.isPending ? 'Saving…' : 'Save outcomes'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {review?.status === 'failed' && (
            <div className="np-banner np-tone-danger mb-3">
              <i className="ri-error-warning-line"></i>
              <span>The automatic review couldn't finish ({review.error || 'unknown error'}) - a human reviewer will look at this directly instead.</span>
            </div>
          )}

          <div className="d-flex gap-2">
            <button type="button" className="np-btn np-btn-ghost" onClick={startNewSubmission}>
              Log another use case
            </button>
            {submittedUseCaseId && (
              <button type="button" className="np-btn np-btn-ghost" onClick={() => navigate(`/ai-use-cases/${submittedUseCaseId}`)}>
                View submission <i className="ri-arrow-right-line"></i>
              </button>
            )}
          </div>
        </>
      )}

      {screen === 'wizard' && mySubmissions.length > 0 && (
        <div className="np-card mt-3">
          <div className="np-card-head"><h2>Your recent submissions</h2></div>
          <div className="np-card-body p-0">
            <table className="np-table">
              <thead><tr><th>Name</th><th>Status</th><th></th></tr></thead>
              <tbody>
                {mySubmissions.slice(0, 5).map((uc) => (
                  <tr key={uc.useCaseId}>
                    <td>{uc.name || 'Untitled'}</td>
                    <td>
                      <span className={`np-chip np-tone-${uc.status === 'active' ? 'success' : uc.status === 'approved' ? 'info' : uc.status === 'rejected' ? 'danger' : 'neutral'}`}>
                        {uc.status}
                      </span>
                    </td>
                    <td>
                      <Link to={`/ai-use-cases/${uc.useCaseId}`} className="np-btn np-btn-ghost np-btn-sm">
                        View <i className="ri-arrow-right-line"></i>
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default HubAiUseCaseWizard;
